import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Button from '@atlaskit/button/default/button';

import { useSetErrorMessage } from '../../providers/AiBulkNavigationContextProvider.tsx';
import { useCreateAllIssues } from '../../services/useCreateAllIssues.tsx';
import {
	useBulkCreateContext,
	useResetSingleIssuesCreated,
} from '../../providers/BulkCreateContextProvider.tsx';
import { useBulkCreateFlags } from '../../services/useBulkCreateFlags.tsx';
import { useBulkCreateLoggingEvents } from '../../utils/useBulkCreateLoggingEvents.tsx';
import { messages } from '../../messages.ts';
import {
	insertContentAtSelectionEnd,
	insertMultipleContentAtSelectionEnd,
	updateLastFetchTimeIfNotViewMode,
	insertWorkItemsIntoTable,
	getSummaryInsertionColumn,
} from '../../services/issueInsertionUtils.tsx';
import { useGetIssueInsertionActions } from '../../services/useGetIssueInsertionActions.tsx';
import { useIssueCreateSidePanelContext } from '../../providers/IssueCreateSidePanelContextProvider.tsx';
import type { CreateIssueResponse } from '../../__types__/apiUtils.ts';
import type { InsertStorageFragmentRequestPayload } from '../../__types__/InsertStorageFragmentRequestPayload.ts';
import type { InsertMultipleStorageFragmentsRequestPayload } from '../../__types__/InsertMultipleStorageFragmentsRequestPayload.ts';
import type { InsertStorageColumnTableRequestPayload } from '../../__types__/InsertStorageColumnTableRequestPayload.ts';
import { useGetUnmappedRequiredFields } from '../../services/useGetUnmappedRequiredFields.tsx';
import { useMappingsState } from '../../providers/BulkConfigureContextProvider.tsx';
import { useUnmappedWorkflowValidators } from '../../services/useUnmappedWorkflowValidators.tsx';

const buttonGroupStyles = xcss({
	paddingTop: 'space.050',
});

// Bulk create button group - used for both AI and non-AI bulk create
export const BulkCreateButtonGroup = ({
	onClose,
	onCreateAll,
}: {
	onClose: () => void;
	onCreateAll: () => void;
}): React.JSX.Element => {
	const { createAllIssuesHandler, loading: isCreateAllLoading } = useCreateAllIssues();
	const [
		{
			currentJiraSite,
			bulkIssuesList,
			insertStorageFragmentRequestPayload,
			singleIssuesCreated,
			selectedTableAdf,
			isCreatedWithAi,
			isCreatedFromActionItems,
			isCreatedFromTable,
		},
		{ removeBulkIssueList },
	] = useBulkCreateContext();
	const [
		{
			insertMultipleStorageFragmentsRequestPayload,
			insertStorageColumnTableRequestPayload,
			pageMode,
			hasEditPermission,
			editorAPI,
		},
	] = useIssueCreateSidePanelContext();
	const {
		triggerMultipleIssuesCreatedFlag,
		triggerSingleIssueInsertLinkFailureFlag,
		triggerMultipleIssuesInsertLinkFailureFlag,
		triggerDocumentModifiedSingleIssueInsertLinkFailureFlag,
		triggerDocumentModifiedMultipleIssuesInsertLinkFailureFlag,
	} = useBulkCreateFlags();
	const { columnToFieldMappings } = useMappingsState();
	const setErrorMessage = useSetErrorMessage();
	const {
		logCreateAllClick,
		logMultipleIssueCreateSuccess,
		logIssueCreationFailed,
		logIssueInsertionFailure,
	} = useBulkCreateLoggingEvents();
	const {
		constructAnchorLinkToHeading,
		constructXmlModification,
		constructXmlModifications,
		constructCellModifications,
	} = useGetIssueInsertionActions();
	const resetSingleIssuesCreated = useResetSingleIssuesCreated();

	const reloadOnClick = useCallback(() => {
		// eslint-disable-next-line no-restricted-syntax
		window.location.href = constructAnchorLinkToHeading();
		window.location.reload();
	}, [constructAnchorLinkToHeading]);
	const unmappedRequiredFields = useGetUnmappedRequiredFields();
	const hasUnmappedRequiredFields = unmappedRequiredFields.length > 0;
	const unmappedWorkflowValidatorFields = useUnmappedWorkflowValidators();
	const hasUnmappedWorkflowValidatorFields = unmappedWorkflowValidatorFields.length > 0;

	const isCreateAllDisabled =
		bulkIssuesList.length === 0 ||
		hasUnmappedRequiredFields ||
		hasUnmappedWorkflowValidatorFields ||
		((isCreatedFromTable || isCreatedFromActionItems) && bulkIssuesList.length > 50);

	const handleInsertionError = useCallback(
		(_error: unknown, createdIssues: CreateIssueResponse[]) => {
			const isDocumentChangedError = (_error as Error)?.message === 'document-changed';

			if (isDocumentChangedError) {
				if (createdIssues.length === 1) {
					triggerDocumentModifiedSingleIssueInsertLinkFailureFlag({
						issueKey: createdIssues[0].key,
						summary: createdIssues[0].summary,
					});
				} else {
					triggerDocumentModifiedMultipleIssuesInsertLinkFailureFlag(createdIssues);
				}
			} else if (createdIssues.length === 1) {
				triggerSingleIssueInsertLinkFailureFlag({
					issueKey: createdIssues[0].key,
					summary: createdIssues[0].summary,
				});
			} else {
				triggerMultipleIssuesInsertLinkFailureFlag(createdIssues);
			}
			logIssueInsertionFailure(createdIssues.length, _error);
		},
		[
			logIssueInsertionFailure,
			triggerDocumentModifiedMultipleIssuesInsertLinkFailureFlag,
			triggerDocumentModifiedSingleIssueInsertLinkFailureFlag,
			triggerMultipleIssuesInsertLinkFailureFlag,
			triggerSingleIssueInsertLinkFailureFlag,
		],
	);

	const handleStorageInsertion = useCallback(
		(
			createdIssues: CreateIssueResponse[],
			payload:
				| InsertStorageFragmentRequestPayload
				| InsertStorageColumnTableRequestPayload
				| InsertMultipleStorageFragmentsRequestPayload,
		) => {
			let showReloadAction = false;
			updateLastFetchTimeIfNotViewMode(payload, pageMode);
			let insertPromise = Promise.resolve(false);
			// insert content at selection end if the user uses AI creation without selecting any content
			if (isCreatedWithAi && !isCreatedFromTable && !isCreatedFromActionItems) {
				insertPromise = insertContentAtSelectionEnd(payload as InsertStorageFragmentRequestPayload);
				// otherwise insert each issue after the corresponding content
			} else if (isCreatedFromActionItems) {
				insertPromise = insertMultipleContentAtSelectionEnd(
					payload as InsertMultipleStorageFragmentsRequestPayload,
				);
			} else if (isCreatedFromTable) {
				const summaryInsertionColumn = getSummaryInsertionColumn(
					columnToFieldMappings,
					isCreatedWithAi,
				);
				insertPromise = insertWorkItemsIntoTable({
					createdIssues,
					currentJiraSiteUrl: currentJiraSite?.url,
					pageMode,
					selectedTableAdf,
					editorAPI,
					payload: payload as InsertStorageColumnTableRequestPayload,
					summaryInsertionColumn,
				});
			}
			insertPromise
				.then((data) => {
					// There is a case where the API response is OK, but the response data is false.
					// When this happens, the backend has trouble inserting the ticket to the page (likely due to a payload error) and we should show an error toast.
					if (data === false) {
						throw new Error('Failed to insert storage');
					}
				})
				.then(() => {
					showReloadAction = pageMode === 'view';
				})
				.catch((_error) => {
					handleInsertionError(_error, createdIssues);
				})
				.finally(() => {
					void resetSingleIssuesCreated();
					triggerMultipleIssuesCreatedFlag(createdIssues, reloadOnClick, showReloadAction);
				});
		},
		[
			isCreatedFromActionItems,
			isCreatedFromTable,
			isCreatedWithAi,
			pageMode,
			reloadOnClick,
			triggerMultipleIssuesCreatedFlag,
			selectedTableAdf,
			currentJiraSite?.url,
			editorAPI,
			columnToFieldMappings,
			resetSingleIssuesCreated,
			handleInsertionError,
		],
	);

	const onCreateAllClick = useCallback(() => {
		logCreateAllClick();
		setErrorMessage(null);
		void createAllIssuesHandler({
			onComplete: ({ createdIssues, createdIssueIDs, failedIssueIDs }) => {
				logMultipleIssueCreateSuccess(createdIssueIDs.length);
				if (isCreatedWithAi && insertStorageFragmentRequestPayload && hasEditPermission) {
					insertStorageFragmentRequestPayload.xmlModification = constructXmlModification(
						[...createdIssues, ...singleIssuesCreated],
						insertStorageFragmentRequestPayload,
					);
					handleStorageInsertion(
						[...createdIssues, ...singleIssuesCreated],
						insertStorageFragmentRequestPayload,
					);
				} else if (
					isCreatedFromTable &&
					insertStorageColumnTableRequestPayload &&
					hasEditPermission
				) {
					insertStorageColumnTableRequestPayload.cellModifications = constructCellModifications([
						...createdIssues,
						...singleIssuesCreated,
					]);
					handleStorageInsertion(
						[...createdIssues, ...singleIssuesCreated],
						insertStorageColumnTableRequestPayload,
					);
				} else if (
					!isCreatedFromTable &&
					isCreatedFromActionItems &&
					insertMultipleStorageFragmentsRequestPayload &&
					hasEditPermission
				) {
					handleStorageInsertion([...createdIssues, ...singleIssuesCreated], {
						pageId: insertMultipleStorageFragmentsRequestPayload.pageId,
						lastFetchTime: insertMultipleStorageFragmentsRequestPayload.lastFetchTime,
						fragments: constructXmlModifications(
							[...createdIssues, ...singleIssuesCreated],
							insertMultipleStorageFragmentsRequestPayload.fragments,
						),
					});
				} else {
					triggerMultipleIssuesCreatedFlag(
						[...createdIssues, ...singleIssuesCreated],
						reloadOnClick,
						false,
					);
				}
				void removeBulkIssueList(createdIssueIDs);
				if (failedIssueIDs.length !== 0) {
					logIssueCreationFailed(failedIssueIDs.length);
					void setErrorMessage({
						createdCount: createdIssueIDs.length,
						failedCount: failedIssueIDs.length,
					});
				} else {
					onCreateAll();
				}
			},
			onFailure: (error: Error | unknown) => {
				logIssueCreationFailed(bulkIssuesList.length, error);
				void setErrorMessage({ createdCount: 0, failedCount: bulkIssuesList.length });
			},
		});
	}, [
		logCreateAllClick,
		setErrorMessage,
		createAllIssuesHandler,
		logMultipleIssueCreateSuccess,
		hasEditPermission,
		isCreatedFromActionItems,
		insertMultipleStorageFragmentsRequestPayload,
		insertStorageColumnTableRequestPayload,
		isCreatedWithAi,
		insertStorageFragmentRequestPayload,
		isCreatedFromTable,
		removeBulkIssueList,
		constructXmlModification,
		singleIssuesCreated,
		handleStorageInsertion,
		constructCellModifications,
		constructXmlModifications,
		triggerMultipleIssuesCreatedFlag,
		reloadOnClick,
		logIssueCreationFailed,
		onCreateAll,
		bulkIssuesList,
	]);

	return (
		<Inline space="space.150" xcss={buttonGroupStyles}>
			<Button appearance="subtle" onClick={onClose} testId="bulk-create-button-group-cancel">
				<FormattedMessage {...messages.bulkCreateCancelFooterButton} />
			</Button>
			<Button
				appearance="primary"
				isDisabled={isCreateAllDisabled}
				isLoading={isCreateAllLoading}
				onClick={onCreateAllClick}
				testId="bulk-create-button-group-create-all"
			>
				<FormattedMessage {...messages.bulkCreateCreateAllFooterButton} />
			</Button>
		</Inline>
	);
};
